import { api } from './api';
import type {
  ProjectWithRelations,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectFilters,
  ProjectsResponse,
  ProjectResponse,
  ProjectStatusResponse,
  ProjectTasksResponse,
  ProjectNotesResponse,
  ProjectStatus,
} from '../types/project';

const PROJECTS_URL = '/projects';

/**
 * Get all projects with filters and pagination
 */
export const getProjects = async (filters?: ProjectFilters): Promise<ProjectsResponse> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.status?.length) {
      params.append('status', filters.status.join(','));
    }
    if (filters.clientId) {
      params.append('clientId', filters.clientId);
    }
    if (filters.tags?.length) {
      params.append('tags', filters.tags.join(','));
    }
    if (filters.includeArchived) {
      params.append('includeArchived', 'true');
    }
    if (filters.dueBefore) {
      params.append('dueBefore', filters.dueBefore);
    }
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString ? `${PROJECTS_URL}?${queryString}` : PROJECTS_URL;

  const response = await api.get<ProjectsResponse>(url);
  return response.data;
};

/**
 * Get a single project by ID
 */
export const getProject = async (id: string): Promise<ProjectResponse> => {
  const response = await api.get<ProjectResponse>(`${PROJECTS_URL}/${id}`);
  return response.data;
};

/**
 * Create a new project
 */
export const createProject = async (data: CreateProjectRequest): Promise<ProjectResponse> => {
  const response = await api.post<ProjectResponse>(PROJECTS_URL, data);
  return response.data;
};

/**
 * Update a project
 */
export const updateProject = async (
  id: string,
  data: UpdateProjectRequest
): Promise<ProjectResponse> => {
  const response = await api.patch<ProjectResponse>(`${PROJECTS_URL}/${id}`, data);
  return response.data;
};

/**
 * Delete a project
 */
export const deleteProject = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`${PROJECTS_URL}/${id}`);
  return response.data;
};

/**
 * Update project status
 */
export const updateProjectStatus = async (
  id: string,
  status: ProjectStatus
): Promise<ProjectStatusResponse> => {
  const response = await api.patch<ProjectStatusResponse>(
    `${PROJECTS_URL}/${id}/status`,
    { status }
  );
  return response.data;
};

/**
 * Get tasks for a project
 */
export const getProjectTasks = async (
  projectId: string,
  filters?: { status?: string }
): Promise<ProjectTasksResponse> => {
  const params = new URLSearchParams();
  if (filters?.status) {
    params.append('status', filters.status);
  }

  const queryString = params.toString();
  const url = queryString
    ? `${PROJECTS_URL}/${projectId}/tasks?${queryString}`
    : `${PROJECTS_URL}/${projectId}/tasks`;

  const response = await api.get<ProjectTasksResponse>(url);
  return response.data;
};

/**
 * Get notes for a project
 */
export const getProjectNotes = async (projectId: string): Promise<ProjectNotesResponse> => {
  const response = await api.get<ProjectNotesResponse>(`${PROJECTS_URL}/${projectId}/notes`);
  return response.data;
};

/**
 * Get projects for a specific client
 */
export const getClientProjects = async (
  clientId: string
): Promise<{ success: boolean; data: { projects: ProjectWithRelations[] } }> => {
  const response = await api.get(`/clients/${clientId}/projects`);
  return response.data;
};

export default {
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  updateProjectStatus,
  getProjectTasks,
  getProjectNotes,
  getClientProjects,
};
